import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { apiClient } from '../lib/api-client';
import { AlertTriangle, History } from 'lucide-react';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { UptimeHistoryDialog } from '../components/UptimeHistoryDialog';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';

type RiskLevel = 'critical' | 'important' | 'moderate' | 'low';

type Incident = {
  source: string;
  severity: 'alert' | 'alarm';
  risk?: RiskLevel; // For Alertmanager alerts
  tenantCode: string;
  environment?: string;
  serviceId: string;
  serviceName?: string;
  monitorId: string;
  title: string;
  startedAt: string;
  endedAt?: string;
  samples: number;
  description?: string;
};

type GroupedIncidents = {
  environment: string;
  sourceUptime: Incident[];
  sourceAlertmanager: Incident[];
  sourceJira: Incident[];
  sourceGrafana: Incident[];
  sourceOtherBySource: Record<string, Incident[]>;
  sourceAlertmanagerByRisk: {
    critical: Incident[];
    important: Incident[];
    moderate: Incident[];
    low: Incident[];
  };
};

export function AlertsAndAlarms() {
  const { user, accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('alerts', 'read');
  const tenantMetaMap = useTenantMetaMap(!!user && canView);
  const location = useLocation();

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  if (hasNoGroups) return <NoGroupsMessage />;

  const tenantsToQuery = useMemo(() => {
    const list = (selectedTenants && selectedTenants.length > 0) ? selectedTenants : accessibleTenants;
    return (list || []).map((t) => String(t).toLowerCase());
  }, [accessibleTenants, selectedTenants]);

  const [_tenantMeta, setTenantMeta] = useState<Map<string, { name?: string; color?: string }>>(new Map());
  const [tenantData, setTenantData] = useState<Array<{
    tenantCode: string;
    tenantName: string;
    tenantColor?: string;
    current: { alarms: Incident[]; alerts: Incident[] } | null;
    history: { alarms: Incident[]; alerts: Incident[] } | null;
    configuredEnvironmentsAlertmanager?: string[];
    configuredEnvironmentsUptime?: string[];
    error?: string;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewAllDialog, setViewAllDialog] = useState<{ type: 'alarms' | 'alerts' | 'history'; incidents: Incident[]; title: string } | null>(null);
  const [historyDialog, setHistoryDialog] = useState<{ tenantCode: string; tenantName: string; environment: string; source: string } | null>(null);
  const deepLinkHandledRef = useRef(false);

  const deepLink = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const show = String(sp.get('show') || '').toLowerCase();
    const source = String(sp.get('source') || 'any').toLowerCase();
    if (show !== 'alarms' && show !== 'alerts') return null;
    return { show: show as 'alarms' | 'alerts', source };
  }, [location.search]);

  // Mark incident-store pages as active so backend monitoring caches use their active refresh cadence.
  useActivityHeartbeat('cache:service_health', !!user && canView);
  useActivityHeartbeat('cache:alerts', !!user && canView);

  const fetchData = useCallback(async (isBackgroundRefresh = false) => {
    if (!canView) return;
    // Only show loading skeleton on initial load, not on background refreshes
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      const tenantsResponse = await apiClient.getTenants();
      const tenantsArr = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const meta = new Map<string, { name?: string; color?: string }>(
        tenantsArr.map((t: any) => [String(t.code || '').toLowerCase(), { name: t.name, color: t.color }]),
      );
      setTenantMeta(meta);

      const results = await Promise.all(
        (tenantsToQuery || []).map(async (tc) => {
          try {
            const [cur, alertsRes, alarmsRes] = await Promise.all([
              apiClient.getMonitoringCurrent(tc),
              apiClient.getMonitoringAlerts(tc),
              apiClient.getMonitoringAlarms(tc),
            ]);
            return {
              tenantCode: tc,
              current: {
                alarms: cur?.current?.alarms || [],
                alerts: cur?.current?.alerts || [],
              },
              history: {
                alerts: alertsRes?.alerts || [],
                alarms: alarmsRes?.alarms || [],
              },
              configuredEnvironmentsAlertmanager: cur?.sources?.alertmanager?.configuredEnvironments || [],
              configuredEnvironmentsUptime: cur?.sources?.uptime?.configuredEnvironments || [],
              error: undefined,
            };
          } catch (e: any) {
            return {
              tenantCode: tc,
              current: null,
              history: null,
              error: e?.message || 'Failed to load alerts',
            };
          }
        }),
      );

      setTenantData(
        results.map((r) => {
          const m = meta.get(r.tenantCode);
          return {
            tenantCode: r.tenantCode,
            tenantName: m?.name || r.tenantCode.toUpperCase(),
            tenantColor: m?.color,
            current: r.current,
            history: r.history,
              configuredEnvironmentsAlertmanager: r.configuredEnvironmentsAlertmanager,
              configuredEnvironmentsUptime: r.configuredEnvironmentsUptime,
            error: r.error,
          };
        }),
      );
    } catch (e: any) {
      setError(e?.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [canView, tenantsToQuery.join(',')]);

  // Listen for cache events to reload when monitoring data changes
  useCacheEvents({
    enabled: !!user && canView,
    // Use semantic incident-store events instead of raw polling caches (uptime/alertmanager) to avoid noisy refresh.
    cacheNames: ['cache:alerts', 'cache:service_health'],
    onEvent: () => {
      // Refetch data when cache changes (background refresh, no loading state)
      if (canView && tenantsToQuery.length > 0) {
        void fetchData(true); // true = background refresh
      }
    },
  });

  useEffect(() => {
    void fetchData(false); // false = initial load, show loading skeleton
  }, [fetchData]);

  // Deep-link support: allow dashboard cards to open the Alerts page and auto-open the matching list dialog.
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    if (!deepLink) return;
    if (loading) return;
    if (error) return;
    if (!tenantData || tenantData.length === 0) return;

    const incidents: Incident[] = [];
    for (const t of tenantData) {
      const c = t.current;
      if (!c) continue;
      const list = deepLink.show === 'alarms' ? (c.alarms || []) : (c.alerts || []);
      for (const i of list) {
        if (deepLink.source && deepLink.source !== 'any') {
          if (String(i.source || '').toLowerCase() !== deepLink.source) continue;
        }
        incidents.push(i);
      }
    }

    deepLinkHandledRef.current = true;
    if (incidents.length === 0) return;

    const srcLabel = deepLink.source && deepLink.source !== 'any'
      ? `${deepLink.source.charAt(0).toUpperCase()}${deepLink.source.slice(1)}`
      : null;
    const title = `${deepLink.show === 'alarms' ? 'Alarms' : 'Alerts'}${srcLabel ? ` - ${srcLabel}` : ''}`;

    setViewAllDialog({ type: deepLink.show, incidents, title });
  }, [deepLink, loading, error, tenantData]);

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Alerts &amp; Alarms</CardTitle>
        </CardHeader>
        <CardContent>You don&apos;t have permission to view Alerts &amp; Alarms.</CardContent>
      </Card>
    );
  }

  const groupByEnvironment = (incidents: Incident[], configuredEnvs?: string[]): GroupedIncidents[] => {
    const envMap = new Map<string, GroupedIncidents>();
    const configuredSet = (configuredEnvs && configuredEnvs.length > 0) ? new Set(configuredEnvs) : null;
    
    // First, pre-populate configured environments (if any)
    if (configuredEnvs && configuredEnvs.length > 0) {
      for (const env of configuredEnvs) {
        envMap.set(env, { 
          environment: env, 
          sourceUptime: [], 
          sourceAlertmanager: [],
          sourceJira: [],
          sourceGrafana: [],
          sourceOtherBySource: {},
          sourceAlertmanagerByRisk: {
            critical: [],
            important: [],
            moderate: [],
            low: [],
          },
        });
      }
    }
    
    // Then add incidents
    for (const i of incidents) {
      const env = i.environment || 'unknown';
      // If we have an explicit configured environment list, ignore incidents that claim other environments.
      // This prevents stale/persisted incidents (e.g. grafana env=prod) from creating phantom env cards.
      if (configuredSet && !configuredSet.has(env)) continue;
      if (!envMap.has(env)) {
        envMap.set(env, { 
          environment: env, 
          sourceUptime: [], 
          sourceAlertmanager: [],
          sourceJira: [],
          sourceGrafana: [],
          sourceOtherBySource: {},
          sourceAlertmanagerByRisk: {
            critical: [],
            important: [],
            moderate: [],
            low: [],
          },
        });
      }
      const group = envMap.get(env)!;
      if (i.source === 'uptime') {
        group.sourceUptime.push(i);
      } else if (i.source === 'alertmanager') {
        group.sourceAlertmanager.push(i);
        // Group by risk level for Alertmanager
        const risk = i.risk || 'low';
        group.sourceAlertmanagerByRisk[risk].push(i);
      } else if (i.source === 'jira') {
        group.sourceJira.push(i);
      } else if (i.source === 'grafana') {
        group.sourceGrafana.push(i);
      } else {
        if (!group.sourceOtherBySource[i.source]) group.sourceOtherBySource[i.source] = [];
        group.sourceOtherBySource[i.source].push(i);
      }
    }

    // Sort: prod first, then alphabetically
    return Array.from(envMap.values()).sort((a, b) => {
      if (a.environment === 'prod') return -1;
      if (b.environment === 'prod') return 1;
      return a.environment.localeCompare(b.environment);
    });
  };

  const renderIncident = (i: Incident, severity: 'alarm' | 'alert', idx?: number) => {
    const isAlarm = severity === 'alarm';
    const borderColor = isAlarm ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-orange-500 bg-orange-50 dark:bg-orange-900/10';
    const textColor = isAlarm ? 'text-red-700 dark:text-red-300' : 'text-orange-700 dark:text-orange-300';
    
    // Generate unique key including environment to avoid duplicates
    const uniqueKey = idx !== undefined 
      ? `${i.source}-${i.environment || 'noenv'}-${i.monitorId}-${i.startedAt}-${idx}`
      : `${i.source}-${i.environment || 'noenv'}-${i.monitorId}-${i.startedAt}`;
    
    return (
      <div key={uniqueKey} className={`rounded-lg border-l-4 ${borderColor} p-3`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className={`text-sm font-bold ${textColor} uppercase`}>{severity}</div>
          <Badge variant="outline" className="text-xs">
            {String(i.source || '').charAt(0).toUpperCase() + String(i.source || '').slice(1)}
          </Badge>
        </div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{i.title}</div>
        {i.description && (
          <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 italic">
            {i.description}
          </div>
        )}
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          {i.environment && <span className="font-medium">{i.environment} · </span>}
          <span>{i.serviceName || i.serviceId}</span>
          <span className="mx-1">·</span>
          <span>since {new Date(i.startedAt).toLocaleString()}</span>
        </div>
      </div>
    );
  };

  const totalAlarms = tenantData.reduce((sum, t) => {
    const c = t.current;
    const alarms = c?.alarms || [];
    const configuredAny = Array.from(new Set([...(t.configuredEnvironmentsAlertmanager || []), ...(t.configuredEnvironmentsUptime || [])]));
    if (configuredAny.length === 0) return sum + alarms.length;
    const allowed = new Set(configuredAny);
    return sum + alarms.filter((i) => allowed.has(i.environment || 'unknown')).length;
  }, 0);

  const totalAlerts = tenantData.reduce((sum, t) => {
    const c = t.current;
    const alerts = c?.alerts || [];
    const configuredAny = Array.from(new Set([...(t.configuredEnvironmentsAlertmanager || []), ...(t.configuredEnvironmentsUptime || [])]));
    if (configuredAny.length === 0) return sum + alerts.length;
    const allowed = new Set(configuredAny);
    return sum + alerts.filter((i) => allowed.has(i.environment || 'unknown')).length;
  }, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={AlertTriangle}
        iconColor="text-red-600 dark:text-red-400"
        iconBgColor="bg-red-100 dark:bg-red-900/30"
        description="Current and historical incidents across selected tenants"
        count={totalAlarms + totalAlerts}
        countLabel={`${totalAlarms} alarms, ${totalAlerts} alerts`}
        countColor="text-red-600 dark:text-red-400"
      />

      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

      {loading && (
        <div className="space-y-6">
          {[1, 2].map((i) => (
            <div key={i} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Skeleton variant="circle" className="w-10 h-10" />
                  <Skeleton className="h-6 w-32" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                {[1, 2].map((j) => (
                  <Card key={j} className="border-l-4">
                    <CardHeader className="pb-3">
                      <Skeleton className="h-5 w-32" />
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-6 w-16 rounded-full" />
                      </div>

                      {[1, 2, 3].map((k) => (
                        <div key={k} className="space-y-2">
                          <Skeleton className="h-3 w-20" />
                          <div className="space-y-2">
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-10 w-full" />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <Skeleton className="h-5 w-32" />
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-8 w-24" />
                    </div>
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((k) => (
                        <div key={k} className="space-y-2">
                          <Skeleton className="h-4 w-2/3" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-6">
          {tenantData.map((t) => {
            const c = t.current;
            const tenantMeta = resolveTenantMeta(tenantMetaMap, t.tenantCode, {
              name: t.tenantName,
              color: t.tenantColor,
            });
            
            const currentAlarms = c?.alarms || [];
            const currentAlerts = c?.alerts || [];
            
            // Group all current incidents by environment
            const allCurrent = [...currentAlarms, ...currentAlerts];
            const configuredUptime = t.configuredEnvironmentsUptime || [];
            const configuredAlertmanager = t.configuredEnvironmentsAlertmanager || [];
            const configuredAny = Array.from(new Set([...(configuredUptime || []), ...(configuredAlertmanager || [])]));
            const groupedByEnv = groupByEnvironment(allCurrent, configuredAny);

            // Hide tenant label if it would render no environment cards (and no error card).
            const hasAnyEnvCards = groupedByEnv.some((group) => {
              const uptimeEnabled = configuredUptime.includes(group.environment);
              const alertmanagerEnabled = configuredAlertmanager.includes(group.environment);
              const hasUptimeData = (group.sourceUptime || []).length > 0;
              const hasAmData = (group.sourceAlertmanager || []).length > 0;
              const otherSources = Object.entries(group.sourceOtherBySource || {});
              const hasOtherData =
                otherSources.some(([_, list]) => (list || []).length > 0) ||
                (group.sourceJira || []).length > 0 ||
                (group.sourceGrafana || []).length > 0;
              const showUptimeSection = uptimeEnabled || hasUptimeData;
              const showAlertmanagerSection = alertmanagerEnabled || hasAmData;
              const showOtherSection = hasOtherData;
              return showUptimeSection || showAlertmanagerSection || showOtherSection;
            });
            if (!t.error && !hasAnyEnvCards) return null;
            
            return (
              <div key={t.tenantCode} className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <TenantHeading
                    tenant={tenantMeta}
                    title={tenantMeta.name}
                    subtitle={tenantMeta.code}
                  />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">
                    {currentAlarms.length + currentAlerts.length} active
                  </span>
                </div>

                {t.error && (
                  <Card>
                    <CardContent className="py-6 text-sm text-red-600 dark:text-red-400">
                      {t.error}
                    </CardContent>
                  </Card>
                )}

                {!t.error && (
                  <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
                    {/* Environment Cards - one per environment */}
                    {groupedByEnv.map((group) => {
                      const uptimeAlarms = group.sourceUptime.filter(i => i.severity === 'alarm');
                      const uptimeAlerts = group.sourceUptime.filter(i => i.severity === 'alert');
                      const amAlarms = group.sourceAlertmanager.filter(i => i.severity === 'alarm');
                      const amAlerts = group.sourceAlertmanager.filter(i => i.severity === 'alert');
                      const jiraAlarms = group.sourceJira.filter(i => i.severity === 'alarm');
                      const jiraAlerts = group.sourceJira.filter(i => i.severity === 'alert');
                      const grafanaAlarms = group.sourceGrafana.filter(i => i.severity === 'alarm');
                      const grafanaAlerts = group.sourceGrafana.filter(i => i.severity === 'alert');
                      
                      const hasUptimeData = uptimeAlarms.length > 0 || uptimeAlerts.length > 0;
                      const hasAmData = group.sourceAlertmanager.length > 0;
                      const otherSources = Object.entries(group.sourceOtherBySource || {});
                      const otherAlarms = otherSources.flatMap(([_, list]) => (list || []).filter(i => i.severity === 'alarm'));
                      const otherAlerts = otherSources.flatMap(([_, list]) => (list || []).filter(i => i.severity === 'alert'));
                      const envAlarms = [...uptimeAlarms, ...jiraAlarms, ...grafanaAlarms, ...amAlarms, ...otherAlarms];
                      const envAlerts = [...uptimeAlerts, ...jiraAlerts, ...grafanaAlerts, ...amAlerts, ...otherAlerts];
                      const hasOtherData = otherSources.some(([_, list]) => (list || []).length > 0);

                      // A card must exist if either Uptime or Alertmanager is enabled for this environment.
                      const uptimeEnabled = configuredUptime.includes(group.environment);
                      const alertmanagerEnabled = configuredAlertmanager.includes(group.environment);

                      const showUptimeSection = uptimeEnabled || hasUptimeData;
                      const showAlertmanagerSection = alertmanagerEnabled || hasAmData;
                      const showOtherSection = hasOtherData || group.sourceJira.length > 0 || group.sourceGrafana.length > 0;

                      // Show card if enabled (or if we have incidents but no config metadata)
                      if (!showUptimeSection && !showAlertmanagerSection && !showOtherSection) return null;
                      
                      // Card color based on highest severity (now including Jira and Grafana)
                      const hasAlarms = uptimeAlarms.length > 0 || amAlarms.length > 0 || jiraAlarms.length > 0 || grafanaAlarms.length > 0;
                      
                      return (
                        <Card key={group.environment} style={hasAlarms ? { animation: 'pulse-red 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base capitalize">{group.environment}</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2.5">
                            {/* Summary (dashboard-style) */}
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => setViewAllDialog({
                                  type: 'alarms',
                                  incidents: envAlarms,
                                  title: `Alarms - ${group.environment}`,
                                })}
                                disabled={envAlarms.length === 0}
                                className="text-center"
                              >
                                <div className={`text-2xl font-bold tabular-nums leading-none ${envAlarms.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>
                                  {envAlarms.length}
                                </div>
                                <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alarms</div>
                              </button>
                              <button
                                type="button"
                                onClick={() => setViewAllDialog({
                                  type: 'alerts',
                                  incidents: envAlerts,
                                  title: `Alerts - ${group.environment}`,
                                })}
                                disabled={envAlerts.length === 0}
                                className="text-center"
                              >
                                <div className={`text-2xl font-bold tabular-nums leading-none ${envAlerts.length > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}>
                                  {envAlerts.length}
                                </div>
                                <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alerts</div>
                              </button>
                            </div>

                            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                              <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">By Source</div>
                              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {([
                                  { key: 'uptime', label: 'uptime', alarms: uptimeAlarms, alerts: uptimeAlerts },
                                  { key: 'jira', label: 'jira', alarms: jiraAlarms, alerts: jiraAlerts },
                                  { key: 'grafana', label: 'grafana', alarms: grafanaAlarms, alerts: grafanaAlerts },
                                ] as const).map((src) => (
                                  <div key={src.key} className="text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase">{src.label}</div>
                                      <button
                                        type="button"
                                        onClick={() => setHistoryDialog({
                                          tenantCode: t.tenantCode,
                                          tenantName: t.tenantName,
                                          environment: group.environment,
                                          source: src.key,
                                        })}
                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                                        title="View history"
                                      >
                                        <History className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                                      </button>
                                    </div>
                                    <div className="mt-1 flex items-baseline justify-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setViewAllDialog({ type: 'alarms', incidents: src.alarms, title: `${src.label} Alarms - ${group.environment}` })}
                                        disabled={src.alarms.length === 0}
                                        className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${src.alarms.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                                      >
                                        {src.alarms.length}
                                      </button>
                                      <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                      <button
                                        type="button"
                                        onClick={() => setViewAllDialog({ type: 'alerts', incidents: src.alerts, title: `${src.label} Alerts - ${group.environment}` })}
                                        disabled={src.alerts.length === 0}
                                        className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${src.alerts.length > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                                      >
                                        {src.alerts.length}
                                      </button>
                                    </div>
                                    <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-500 leading-none">alarms / alerts</div>
                                  </div>
                                ))}

                                {/* Alertmanager (4 numbers) */}
                                <div className="text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase">alertmanager</div>
                                    <button
                                      type="button"
                                      onClick={() => setHistoryDialog({
                                        tenantCode: t.tenantCode,
                                        tenantName: t.tenantName,
                                        environment: group.environment,
                                        source: 'alertmanager',
                                      })}
                                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                                      title="View history"
                                    >
                                      <History className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                                    </button>
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-baseline justify-center gap-x-1.5 gap-y-1">
                                    {(() => {
                                      const c = group.sourceAlertmanagerByRisk.critical.length;
                                      const i = group.sourceAlertmanagerByRisk.important.length;
                                      const m = group.sourceAlertmanagerByRisk.moderate.length;
                                      const l = group.sourceAlertmanagerByRisk.low.length;
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (c > 0) {
                                                setViewAllDialog({ type: 'alerts', incidents: group.sourceAlertmanagerByRisk.critical, title: `Alertmanager critical - ${group.environment}` });
                                              }
                                            }}
                                            disabled={c === 0}
                                            className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${c > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                                            title={`critical (${c})`}
                                          >
                                            {c}
                                          </button>
                                          <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (i > 0) {
                                                setViewAllDialog({ type: 'alerts', incidents: group.sourceAlertmanagerByRisk.important, title: `Alertmanager important - ${group.environment}` });
                                              }
                                            }}
                                            disabled={i === 0}
                                            className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${i > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                                            title={`important (${i})`}
                                          >
                                            {i}
                                          </button>
                                          <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (m > 0) {
                                                setViewAllDialog({ type: 'alerts', incidents: group.sourceAlertmanagerByRisk.moderate, title: `Alertmanager moderate - ${group.environment}` });
                                              }
                                            }}
                                            disabled={m === 0}
                                            className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${m > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400'}`}
                                            title={`moderate (${m})`}
                                          >
                                            {m}
                                          </button>
                                          <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (l > 0) {
                                                setViewAllDialog({ type: 'alerts', incidents: group.sourceAlertmanagerByRisk.low, title: `Alertmanager low - ${group.environment}` });
                                              }
                                            }}
                                            disabled={l === 0}
                                            className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${l > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}
                                            title={`low (${l})`}
                                          >
                                            {l}
                                          </button>
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-500 leading-none">C / I / M / L</div>
                                </div>
                              </div>
                            </div>

                            {/* Other Sources (anything not uptime/alertmanager/jira/grafana) */}
                            {showOtherSection && (
                              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Other</div>
                                <div className="space-y-3">
                                  {otherSources
                                    .filter(([_, list]) => (list || []).length > 0)
                                    .map(([src, list]) => {
                                      const srcAlarms = (list || []).filter(i => i.severity === 'alarm');
                                      const srcAlerts = (list || []).filter(i => i.severity === 'alert');
                                      return (
                                        <div key={src} className="space-y-1.5">
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                              {src.charAt(0).toUpperCase() + src.slice(1)}
                                            </span>
                                            <button
                                              onClick={() => setHistoryDialog({
                                                tenantCode: t.tenantCode,
                                                tenantName: t.tenantName,
                                                environment: group.environment,
                                                source: src,
                                              })}
                                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                                              title="View history"
                                            >
                                              <History className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                            </button>
                                          </div>
                                          <div className="flex items-baseline justify-start gap-2">
                                            <button
                                              type="button"
                                              onClick={() => setViewAllDialog({
                                                type: 'alarms',
                                                incidents: srcAlarms,
                                                title: `${src.charAt(0).toUpperCase() + src.slice(1)} Alarms - ${group.environment}`,
                                              })}
                                              disabled={srcAlarms.length === 0}
                                              className={`text-sm font-bold tabular-nums leading-none ${srcAlarms.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                                            >
                                              {srcAlarms.length}
                                            </button>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                            <button
                                              type="button"
                                              onClick={() => setViewAllDialog({
                                                type: 'alerts',
                                                incidents: srcAlerts,
                                                title: `${src.charAt(0).toUpperCase() + src.slice(1)} Alerts - ${group.environment}`,
                                              })}
                                              disabled={srcAlerts.length === 0}
                                              className={`text-sm font-bold tabular-nums leading-none ${srcAlerts.length > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                                            >
                                              {srcAlerts.length}
                                            </button>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">alarms / alerts</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View All Dialog */}
      {viewAllDialog && (
        <Dialog open={true} onOpenChange={() => setViewAllDialog(null)}>
          <DialogContent maxWidth="2xl" className="flex flex-col max-h-[90vh]">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{viewAllDialog.title}</DialogTitle>
            </DialogHeader>
            <DialogBody className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {viewAllDialog.incidents.map((i, idx) => renderIncident(i, i.severity, idx))}
              </div>
            </DialogBody>
            <DialogFooter className="flex-shrink-0">
              <Button onClick={() => setViewAllDialog(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* History Dialog */}
      {historyDialog && (
        <UptimeHistoryDialog
          open={true}
          onClose={() => setHistoryDialog(null)}
          tenantCode={historyDialog.tenantCode}
          tenantName={historyDialog.tenantName}
          environment={historyDialog.environment}
          source={historyDialog.source}
        />
      )}

      <style>{`
        @keyframes pulse-red {
          0%, 100% {
            background-color: transparent;
          }
          50% {
            background-color: rgba(239, 68, 68, 0.1);
          }
        }
        @media (prefers-color-scheme: dark) {
          @keyframes pulse-red {
            0%, 100% {
              background-color: transparent;
            }
            50% {
              background-color: rgba(239, 68, 68, 0.2);
            }
          }
        }
      `}</style>
    </div>
  );
}





